import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import styles from '../styles.module.scss';
import { useContactPicker } from '../ContactPickerContext';
import { useFieldHistory } from '../FieldHistoryContext';
import SyncDropdown from './SyncDropdown';
import EditPlaceholderModal from './EditPlaceholderModal';
import {
  UPDATE_DEAL_FIELD_GROUP,
  CREATE_DEAL_FIELD_GROUP,
  BIND_CONTACT_TO_FIELD_GROUP,
  LINK_PLACEHOLDER_TO_FIELD_GROUP,
  UNLINK_PLACEHOLDER_FROM_FIELD_GROUP,
  UPDATE_PLACEHOLDER_LINK_VALUE,
} from '../../../../api/placeholders/mutations';
import { queriesV2, mutations } from '../../../../api';
import { ReactComponent as ChevronDownIcon } from '../../../../shared/icons/chevron-down.svg';
import { ReactComponent as TrashIcon } from '../../../../shared/icons/trashcan-icon.svg';
import { ReactComponent as EditIcon } from '../../../../shared/icons/edit-icon.svg';
import { ReactComponent as PencilIcon } from '../../../../shared/icons/icon-edit.svg';
import { ReactComponent as UnlinkIcon } from '../../../../shared/icons/chain-broken-icon.svg';

interface PlaceholderItemProps {
  placeholderLink: any;
  dealFieldGroups: any[];
  dealId: string;
  documentId: string;
  teamId: string;
  isReadOnly?: boolean;
  isCounterparty?: boolean; // Simplified view for counterparty team
  canLinkContacts?: boolean;
  onShowUpgradeModal?: () => void;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: () => void;
}

const PlaceholderItem: React.FC<PlaceholderItemProps> = ({
  placeholderLink,
  dealFieldGroups,
  dealId,
  documentId,
  teamId,
  isReadOnly = false,
  isCounterparty = false,
  canLinkContacts = true,
  onShowUpgradeModal,
  isExpanded,
  onToggle,
  onUpdate,
}) => {
  // The field name. It travels with every editor event alongside the uuid: a
  // placeholder that has been through a docx round trip carries only its alias
  // in the body, so the name is the only identity the document can be matched on.
  const title = placeholderLink.placeholder?.title || 'Unnamed Field';

  // Determine current state
  const isLinked = !!placeholderLink.dealFieldGroup;
  const hasContactBinding = isLinked && placeholderLink.dealFieldGroup?.contactBinding?.contact_id;

  // Value source: when linked, use dealFieldGroup.value; when unlinked, use currentValue
  const getValueFromProps = () => {
    return isLinked ? placeholderLink.dealFieldGroup?.value || '' : placeholderLink.currentValue || '';
  };

  const [localValue, setLocalValue] = useState(getValueFromProps);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditingSaving, setIsEditingSaving] = useState(false);
  const { openContactPicker, openEditContact } = useContactPicker();
  const { openHistory } = useFieldHistory();

  // Refetch queries for placeholder completeness validation after mutations
  const completenessRefetchQueries = [
    {
      query: queriesV2.getDocumentPlaceholderCompleteness,
      variables: { documentId: String(documentId), documentType: 'Document' },
    },
  ];

  // GraphQL mutations - all include refetchQueries to keep completeness validation in sync
  const [updateFieldGroup] = useMutation(UPDATE_DEAL_FIELD_GROUP, { refetchQueries: completenessRefetchQueries });
  const [updatePlaceholderLinkValue] = useMutation(UPDATE_PLACEHOLDER_LINK_VALUE, {
    refetchQueries: completenessRefetchQueries,
  });
  const [linkToGroup] = useMutation(LINK_PLACEHOLDER_TO_FIELD_GROUP, { refetchQueries: completenessRefetchQueries });
  const [unlinkFromGroup] = useMutation(UNLINK_PLACEHOLDER_FROM_FIELD_GROUP, {
    refetchQueries: completenessRefetchQueries,
  });
  const [createFieldGroup, { loading: isCreatingFieldGroup }] = useMutation(CREATE_DEAL_FIELD_GROUP, {
    refetchQueries: completenessRefetchQueries,
  });
  const [bindContact] = useMutation(BIND_CONTACT_TO_FIELD_GROUP, { refetchQueries: completenessRefetchQueries });
  const [removeDocumentPlaceholder] = useMutation(mutations.removeDocumentPlaceholder, {
    refetchQueries: completenessRefetchQueries,
  });
  const [updateDocumentPlaceholder] = useMutation(mutations.updateDocumentPlaceholder, {
    refetchQueries: completenessRefetchQueries,
  });

  // Debounce ref for value changes
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  // Track previous value to detect changes from backend (e.g., contact binding)
  const prevValueRef = useRef<string>('');
  // Track the last value we dispatched to the editor - prevents duplicate dispatches
  // Value-based detection is more reliable than time-based (setTimeout) patterns
  const lastDispatchedValueRef = useRef<string>('');

  // Update local value when prop changes and sync to the document
  useEffect(() => {
    // When linked, use dealFieldGroup.value; when unlinked, use currentValue
    const newValue = isLinked ? placeholderLink.dealFieldGroup?.value || '' : placeholderLink.currentValue || '';

    setLocalValue(newValue);

    // Only dispatch if:
    // 1. Value changed from what we last saw (external change)
    // 2. AND it's not a value we already dispatched (prevents duplicate dispatches)
    const isExternalChange = newValue !== prevValueRef.current && newValue !== lastDispatchedValueRef.current;

    if (isExternalChange) {
      const uuid = placeholderLink.placeholder?.uuid;
      if (uuid) {
        window.dispatchEvent(
          new CustomEvent('updatePlaceholderValue', {
            detail: { placeholderId: uuid, placeholderName: title, newValue },
          })
        );
        lastDispatchedValueRef.current = newValue;
      }
    }
    prevValueRef.current = newValue;
  }, [
    isLinked,
    placeholderLink.currentValue,
    placeholderLink.dealFieldGroup?.value,
    placeholderLink.placeholder?.uuid,
  ]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      setLocalValue(newValue);

      // Clear previous debounce timer
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce the save operation
      debounceRef.current = setTimeout(async () => {
        try {
          if (isLinked && placeholderLink.dealFieldGroup?.id) {
            // Linked: update the deal field group (syncs to all linked placeholders)
            await updateFieldGroup({
              variables: {
                id: placeholderLink.dealFieldGroup.id,
                value: newValue,
              },
            });
          } else {
            // Unlinked: update the placeholder link directly
            await updatePlaceholderLinkValue({
              variables: {
                placeholderUuid: placeholderLink.placeholder?.uuid,
                linkableId: documentId,
                linkableType: 'Document',
                teamId,
                currentValue: newValue,
              },
            });
          }

          // Write the value into the document.
          const uuid = placeholderLink.placeholder?.uuid;
          if (uuid) {
            // Mark this value as dispatched to prevent duplicate dispatch from useEffect
            lastDispatchedValueRef.current = newValue;
            window.dispatchEvent(
              new CustomEvent('updatePlaceholderValue', {
                detail: { placeholderId: uuid, placeholderName: title, newValue },
              })
            );
          }
          onUpdate();
        } catch (error) {
          // Silent failure - user can retry
        }
      }, 500);
    },
    [isLinked, placeholderLink, documentId, teamId, updateFieldGroup, updatePlaceholderLinkValue, onUpdate]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSyncChange = useCallback(
    async (selectedValue: string) => {
      if (isCreatingFieldGroup) return;

      try {
        if (selectedValue === 'none') {
          // Unlink from any deal field (preserves current value)
          if (isLinked) {
            await unlinkFromGroup({
              variables: { placeholderLinkId: placeholderLink.id },
            });
            onUpdate();
          }
        } else {
          // Link to existing deal field
          await linkToGroup({
            variables: {
              placeholderLinkId: placeholderLink.id,
              dealFieldGroupId: selectedValue,
            },
          });

          // Write the newly linked field's value into the document
          const uuid = placeholderLink.placeholder?.uuid;
          const linkedGroup = dealFieldGroups.find((g: any) => g.id === selectedValue);
          if (uuid && linkedGroup) {
            const newValue = linkedGroup.value || '';
            // Mark this value as dispatched to prevent duplicate dispatch from useEffect
            lastDispatchedValueRef.current = newValue;
            window.dispatchEvent(
              new CustomEvent('updatePlaceholderValue', {
                detail: { placeholderId: uuid, placeholderName: title, newValue, linkedGroupId: linkedGroup.id },
              })
            );
          }
          onUpdate();
        }
      } catch (error) {
        // Silent failure - user can retry
      }
    },
    [isLinked, isCreatingFieldGroup, placeholderLink, dealFieldGroups, linkToGroup, unlinkFromGroup, onUpdate]
  );

  const handleBindContact = useCallback(
    async (contactId: string | null, field: string | null) => {
      if (isCreatingFieldGroup) return;

      try {
        let groupId = placeholderLink.dealFieldGroup?.id;

        // If not linked, create a deal field first
        if (!isLinked) {
          const result = await createFieldGroup({
            variables: {
              dealId,
              name: placeholderLink.placeholder?.title || 'Field',
              value: localValue,
            },
          });
          groupId = result.data?.createDealFieldGroup?.dealFieldGroup?.id;

          // Link to the new group
          await linkToGroup({
            variables: {
              placeholderLinkId: placeholderLink.id,
              dealFieldGroupId: groupId,
            },
          });
        }

        // Bind or unbind contact
        const bindResult = await bindContact({
          variables: {
            dealFieldGroupId: groupId,
            contactId,
            contactField: field,
          },
        });

        const isUnlinking = contactId === null;
        const uuid = placeholderLink.placeholder?.uuid;

        if (isUnlinking && groupId) {
          // Unlinking: clear the value on backend AND locally
          await updateFieldGroup({
            variables: {
              id: groupId,
              value: '',
            },
          });
          setLocalValue('');

          // Clear the value in the document (clear linkedGroupId when unlinking)
          if (uuid) {
            // Mark empty value as dispatched to prevent duplicate dispatch from useEffect
            lastDispatchedValueRef.current = '';
            window.dispatchEvent(
              new CustomEvent('updatePlaceholderValue', {
                detail: { placeholderId: uuid, placeholderName: title, newValue: '', linkedGroupId: null },
              })
            );
          }
        } else if (uuid) {
          // Linking: write the new contact value into the document
          const newValue = bindResult.data?.bindContactToFieldGroup?.dealFieldGroup?.value || '';
          const linkedGroupId = bindResult.data?.bindContactToFieldGroup?.dealFieldGroup?.id || groupId;
          setLocalValue(newValue);
          // Mark this value as dispatched to prevent duplicate dispatch from useEffect
          lastDispatchedValueRef.current = newValue;
          window.dispatchEvent(
            new CustomEvent('updatePlaceholderValue', {
              detail: { placeholderId: uuid, placeholderName: title, newValue, linkedGroupId },
            })
          );
        }

        onUpdate();
      } catch (error) {
        // Silent failure - user can retry
      }
    },
    [
      isLinked,
      isCreatingFieldGroup,
      dealId,
      placeholderLink,
      localValue,
      createFieldGroup,
      linkToGroup,
      bindContact,
      updateFieldGroup,
      onUpdate,
    ]
  );

  // Listen for "linkField" event from balloon popup to auto-expand and show contact picker
  useEffect(() => {
    const placeholderTitle = placeholderLink.placeholder?.title || 'Unnamed Field';
    const handleLinkField = (event: CustomEvent) => {
      if (event.detail?.placeholderId === placeholderLink.placeholder?.uuid) {
        // Dispatch event to parent to expand this item
        window.dispatchEvent(
          new CustomEvent('focusPlaceholderInSidebar', {
            detail: { placeholderId: placeholderLink.placeholder?.uuid },
          })
        );
        // Use requestAnimationFrame to wait for DOM update after accordion expands
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            openContactPicker({
              teamId,
              placeholderName: placeholderTitle,
              onSelect: handleBindContact,
            });
          });
        });
      }
    };

    window.addEventListener('linkFieldToContact' as any, handleLinkField);
    return () => {
      window.removeEventListener('linkFieldToContact' as any, handleLinkField);
    };
  }, [placeholderLink.placeholder?.uuid, teamId, openContactPicker, handleBindContact]);

  const scrollToPlaceholder = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(
      new CustomEvent('scrollToPlaceholder', {
        detail: {
          placeholderId: placeholderLink.placeholder?.uuid,
          // Name fallback: docx-native migrated placeholders carry no uuid in
          // the body SDT, so the docx editor matches on the field name instead.
          placeholderName: placeholderLink.placeholder?.title,
        },
      })
    );
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = useCallback(async () => {
    const uuid = placeholderLink.placeholder?.uuid;
    if (!uuid) return;

    try {
      // Delete the DocumentPlaceholderLink from the database
      await removeDocumentPlaceholder({
        variables: {
          placeholderUuid: uuid,
          linkableId: documentId,
          linkableType: 'Document',
          teamId,
        },
      });

      // Tell the editor to take the control out of the body. The name travels
      // too: a MIGRATED placeholder carries no uuid in the body SDT, only the
      // alias, so the uuid alone would never match one.
      window.dispatchEvent(
        new CustomEvent('deletePlaceholderFromSidebar', {
          detail: { placeholderId: uuid, placeholderName: placeholderLink.placeholder?.title },
        })
      );

      // Refetch to update the sidebar list (mutation already deleted from DB)
      onUpdate();
    } catch (error) {
      // If backend deletion fails, still remove it from the document so the
      // two views don't disagree.
      window.dispatchEvent(
        new CustomEvent('deletePlaceholderFromSidebar', {
          detail: { placeholderId: uuid, placeholderName: placeholderLink.placeholder?.title },
        })
      );
    }
    setShowDeleteModal(false);
  }, [placeholderLink.placeholder?.uuid, documentId, teamId, removeDocumentPlaceholder, onUpdate]);

  const displayValue = localValue;

  const handleOpenContactPicker = () => {
    if (!canLinkContacts) {
      onShowUpgradeModal?.();
      return;
    }
    openContactPicker({
      teamId,
      placeholderName: title,
      onSelect: handleBindContact,
    });
  };

  const handleEditContact = () => {
    const contact = placeholderLink.dealFieldGroup?.boundContact;
    if (contact) {
      openEditContact(contact);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowEditModal(true);
  };

  const handleSaveEdit = useCallback(
    async (newName: string, newRequired: boolean) => {
      const uuid = placeholderLink.placeholder?.uuid;
      if (!uuid) return;

      setIsEditingSaving(true);
      try {
        await updateDocumentPlaceholder({
          variables: {
            placeholderUuid: uuid,
            linkableId: documentId,
            linkableType: 'Document',
            teamId,
            title: newName,
            isRequired: newRequired,
          },
        });

        // Rename the control in the document too. `previousName` is what the
        // body can actually be matched on — a placeholder that has been through
        // a docx round trip carries no uuid, only its alias.
        window.dispatchEvent(
          new CustomEvent('updatePlaceholderName', {
            detail: { placeholderId: uuid, previousName: title, newName, isRequired: newRequired },
          })
        );

        onUpdate();
        setShowEditModal(false);
      } catch (error) {
        // Silent failure - user can retry
      } finally {
        setIsEditingSaving(false);
      }
    },
    [placeholderLink.placeholder?.uuid, title, documentId, teamId, updateDocumentPlaceholder, onUpdate]
  );

  return (
    <div className={classNames(styles.accordionItem, { [styles.expanded]: isExpanded })}>
      {/* Collapsed Header - always visible */}
      <div className={styles.accordionHeader} onClick={onToggle}>
        <div className={styles.accordionHeaderTop}>
          <div className={styles.accordionHeaderLeft}>
            <ChevronDownIcon className={classNames(styles.chevronIcon, { [styles.chevronUp]: isExpanded })} />
            <span className={styles.fieldTitle} title={title}>
              {title}
            </span>
          </div>
          {/* Edit and Delete buttons - hidden for counterparty */}
          {!isReadOnly && !isCounterparty && isExpanded && (
            <div className={styles.accordionHeaderRight}>
              <button className={styles.editButton} onClick={handleEditClick} title="Edit placeholder">
                <PencilIcon className={styles.editIcon} />
              </button>
              <button className={styles.trashButton} onClick={handleDeleteClick} title="Delete placeholder">
                <TrashIcon className={styles.trashIcon} />
              </button>
            </div>
          )}
        </div>
        {displayValue && !isExpanded && (
          <span className={styles.valuePreview} title={displayValue}>
            {displayValue}
          </span>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className={styles.accordionContent}>
          {/* Value Section */}
          <div className={styles.valueSection}>
            <div className={styles.valueLabelRow}>
              <label className={styles.fieldLabel}>Value</label>
              <div className={styles.valueLabelActions}>
                {/* Prior values expose the creator side's negotiating position,
                    so the counterparty never sees this. The server refuses them
                    too — this only keeps the affordance out of their way. */}
                {!isCounterparty && placeholderLink.placeholder?.uuid && (
                  <button
                    className={styles.historyButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      openHistory({
                        scope: 'document',
                        title,
                        placeholderUuid: placeholderLink.placeholder.uuid,
                        linkableId: String(documentId),
                      });
                    }}
                  >
                    History
                  </button>
                )}
                {!isCounterparty && placeholderLink.placeholder?.uuid && (
                  <span className={styles.valueLabelDivider} aria-hidden="true">
                    |
                  </span>
                )}
                <button className={styles.viewInDocButton} onClick={scrollToPlaceholder}>
                  View in doc →
                </button>
              </div>
            </div>
            {hasContactBinding ? (
              <>
                {/* Contact Binding Card - read-only view for counterparty */}
                {isReadOnly || isCounterparty ? (
                  <div className={styles.contactValueDisplay}>
                    <span className={styles.contactValue}>{displayValue || '(empty)'}</span>
                  </div>
                ) : (
                  <div className={styles.contactCard} title={displayValue || '(empty)'}>
                    <div className={styles.contactCardValue}>{displayValue || '(empty)'}</div>
                    <div className={styles.contactCardActions}>
                      <button
                        className={styles.iconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditContact();
                        }}
                        title="Edit contact"
                      >
                        <EditIcon className={styles.iconButtonSvg} />
                      </button>
                      <button
                        className={styles.iconButton}
                        onClick={async (e) => {
                          e.stopPropagation();

                          try {
                            // Unlink from deal field group
                            await unlinkFromGroup({
                              variables: { placeholderLinkId: placeholderLink.id },
                            });

                            // Clear the value on the now-unlinked placeholder
                            await updatePlaceholderLinkValue({
                              variables: {
                                placeholderUuid: placeholderLink.placeholder?.uuid,
                                linkableId: documentId,
                                linkableType: 'Document',
                                teamId,
                                currentValue: '',
                              },
                            });

                            // Clear local state
                            setLocalValue('');

                            // Show the placeholder name in the document instead of the old value
                            const uuid = placeholderLink.placeholder?.uuid;
                            if (uuid) {
                              // Mark empty value as dispatched to prevent duplicate dispatch from useEffect
                              lastDispatchedValueRef.current = '';
                              window.dispatchEvent(
                                new CustomEvent('updatePlaceholderValue', {
                                  detail: { placeholderId: uuid, placeholderName: title, newValue: '', linkedGroupId: null },
                                })
                              );
                            }

                            onUpdate();
                          } catch (error) {
                            // Silent failure - user can retry
                          }
                        }}
                        title="Unlink contact"
                      >
                        <UnlinkIcon className={styles.unlinkIconSvg} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <input
                  type="text"
                  className={styles.valueInput}
                  value={localValue}
                  onChange={(e) => handleValueChange(e.target.value)}
                  placeholder="Enter value..."
                  disabled={isReadOnly || isCounterparty}
                />
                {/* Select Contact button - hidden for counterparty */}
                {!isReadOnly && !isCounterparty && (
                  <button className={styles.bindButton} onClick={handleOpenContactPicker}>
                    👤 Select Contact
                  </button>
                )}
              </>
            )}
          </div>

          {/* Sync Across Deal Section - hidden for counterparty */}
          {!isReadOnly && !isCounterparty && (
            <div className={styles.syncSection}>
              <label className={styles.fieldLabel}>Sync across deal</label>
              <SyncDropdown
                value={isLinked ? placeholderLink.dealFieldGroup?.id : 'none'}
                onChange={handleSyncChange}
                disabled={hasContactBinding}
                options={[
                  { value: 'none', label: 'Not synced' },
                  ...dealFieldGroups.map((group: any) => ({
                    value: group.id,
                    label: group.name,
                    subLabel: `${group.value ? group.value + ' — ' : ''}${group.documentCount || 0} doc${(group.documentCount || 0) !== 1 ? 's' : ''}`,
                  })),
                ]}
              />
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.deleteModal} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.deleteModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.deleteModalTitle}>Delete Placeholder</h3>
            <p className={styles.deleteModalText}>Are you sure you want to delete "{title}" from this document?</p>
            <div className={styles.deleteModalActions}>
              <button className={styles.cancelButton} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className={styles.deleteButton} onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Placeholder Modal */}
      <EditPlaceholderModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveEdit}
        currentName={placeholderLink.placeholder?.title || ''}
        currentRequired={placeholderLink.placeholder?.isRequired || false}
        isSaving={isEditingSaving}
      />
    </div>
  );
};

export default PlaceholderItem;
